import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Tab } from "@circle-react-uikit/TabV2";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { PendingConnections } from "@/react/components/MemberDirectory/AllMembers/PendingConnections";
import { Recommendations } from "@/react/components/MemberDirectory/AllMembers/Recommendations";
import { MyConnections } from "@/react/components/MemberDirectory/Connections/MyConnections";
import { MemberCountProvider } from "@/react/components/MemberDirectory/shared/MobileFilters/MemberCountContext";
import { useMemberDirectorySettings } from "@/react/components/MemberDirectory/useMemberDirectorySettings";
import { EMPTY_MEMBER_IMAGES } from "@/react/components/shared/MembersList/CommunityMembers/constants";
import { ProgressiveImage } from "@/react/components/shared/ProgressiveImage";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isMemberDirectoryVisible } from "@/react/helpers/communityMemberHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { invalidateConnectionRequestsQueryCache } from "@/react/hooks/connectionRequests/connectionRequestCacheHelpers";
import {
  useConnectionRequestWebSocket,
  type ConnectionRequestWebSocketEvent,
} from "@/react/hooks/connectionRequests/useConnectionRequestWebSocket";
import { useRecommendationsInfiniteQuery } from "@/react/hooks/recommendations/useRecommendationsInfiniteQuery";
import { useIsConnectRecommendationsEnabled } from "@/react/hooks/useIsConnectEnabled";
import { usePendingConnections } from "@/react/hooks/usePendingConnections";
import { useThemeContext } from "@/react/providers/Theme";

interface BodyUIKitProps {
  onClose?: () => void;
}

export const BodyUIKit = ({ onClose }: BodyUIKitProps) => {
  const navigate = useNavigate();
  const memberDirectorySettings = useMemberDirectorySettings();
  const queryClient = useQueryClient();
  const { currentAppearance } = useThemeContext();

  const { currentCommunityMember, currentCommunitySettings } =
    usePunditUserContext();

  const shouldShowCtaOnEmptyState = isMemberDirectoryVisible(
    currentCommunityMember,
    currentCommunitySettings,
  );

  const pendingConnectionsQuery = usePendingConnections({
    perPage: 2,
  });

  const isConnectRecommendationsEnabled = useIsConnectRecommendationsEnabled();
  const shouldFetchRecommendations = Boolean(
    isConnectRecommendationsEnabled && currentCommunityMember,
  );

  const recommendationsQuery = useRecommendationsInfiniteQuery({
    perPage: 2,
    scope: "connect-tab-header",
    options: {
      enabled: shouldFetchRecommendations,
    },
  });

  const handleWebSocketMessage = useCallback(
    (eventData: ConnectionRequestWebSocketEvent) => {
      if (eventData.event === "updatePendingRequestsCount") {
        invalidateConnectionRequestsQueryCache({ queryClient });
        void pendingConnectionsQuery.refetch();
      } else if (eventData.event === "connectionStatusChanged") {
        invalidateConnectionRequestsQueryCache({
          queryClient,
          initiatorPublicUid: eventData.initiator_public_uid,
          recipientPublicUid: eventData.recipient_public_uid,
        });
        void pendingConnectionsQuery.refetch();
        void recommendationsQuery.refetch();
      }
    },
    [queryClient, pendingConnectionsQuery, recommendationsQuery],
  );

  useConnectionRequestWebSocket(
    currentCommunityMember?.id,
    handleWebSocketMessage,
  );

  const shouldShowEmptyState =
    pendingConnectionsQuery.audience.length === 0 &&
    recommendationsQuery.recommendations.length === 0 &&
    !pendingConnectionsQuery.isLoading &&
    !recommendationsQuery.isLoading;

  const emptyMembersImageVariants = EMPTY_MEMBER_IMAGES[currentAppearance];

  const tabClassName = classNames(
    "!text-dark text-sm flex-1 rounded-md !border-none text-center p-0 h-9 ",
  );
  const activeClassName = classNames(
    "!bg-white dark:!bg-black shadow-xs text-sm",
  );

  const textClassName = classNames("text-dark font-medium");

  return (
    <Tab.Group className="bg-primary flex flex-1 flex-col overflow-hidden min-h-0">
      <div className="px-4 mt-4">
        <Tab.List className="bg-secondary rounded-md flex p-1.5 m-0">
          <Tab
            className={tabClassName}
            activeClassName={activeClassName}
            wrapperClassName="flex-1"
            textClassName={textClassName}
            data-testid="connect-panel-tab-discover"
          >
            {t("header_v3.connect.tabs.discover.title")}
          </Tab>
          <Tab
            className={tabClassName}
            activeClassName={activeClassName}
            wrapperClassName="flex-1 p-0"
            textClassName={textClassName}
            data-testid="connect-panel-tab-my-connections"
          >
            {t("header_v3.connect.tabs.my_connections.title")}
          </Tab>
        </Tab.List>
      </div>
      <Tab.Panels className="flex-1 min-h-0">
        <Tab.Panel>
          <div
            className="p-4 flex flex-col gap-4"
            data-testid="connect-tab-discover"
          >
            <PendingConnections
              pendingConnectionsQuery={pendingConnectionsQuery}
            />
            {isConnectRecommendationsEnabled && (
              <Recommendations recommendationsQuery={recommendationsQuery} />
            )}

            {shouldShowEmptyState && (
              <div className="flex flex-col items-center justify-center">
                <div className="mt-14 flex flex-1 items-center justify-center">
                  <ProgressiveImage
                    className="h-36"
                    src={emptyMembersImageVariants.large}
                    srcVariants={emptyMembersImageVariants}
                    alt={t("members_directory.no_members.title")}
                    imageClassName="object-contain"
                  />
                </div>
                <EmptyState
                  header={t(
                    "header_v3.connect.tabs.discover.empty_state.title",
                  )}
                  description={t(
                    `header_v3.connect.tabs.discover.empty_state.${shouldShowCtaOnEmptyState ? "description_with_cta" : "description"}`,
                  )}
                  secondaryLabel={
                    shouldShowCtaOnEmptyState
                      ? t("header_v3.connect.tabs.discover.empty_state.cta")
                      : undefined
                  }
                  onSecondaryClick={() => {
                    void navigate({ to: "/members" });
                    onClose?.();
                  }}
                />
              </div>
            )}
          </div>
        </Tab.Panel>
        <Tab.Panel>
          <div
            className="p-4 flex flex-col gap-4"
            data-testid="connect-tab-my-connections"
          >
            <MemberCountProvider>
              <MyConnections
                memberDirectorySettings={memberDirectorySettings}
              />
            </MemberCountProvider>
          </div>
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
};
